// Charts for workbooks and dashboards, drawn as SVG: column, stacked column, bar, line, area and donut.
// One value axis that starts at zero, thin marks (bars at most 24px, 2px lines), a 2px surface gap between touching
// marks, a legend whenever there's more than one series, a target line when asked, and a tooltip with every
// series' value for the hovered category. Colors are assigned by series position in a fixed order and validated
// for color-vision deficiency; past eight series the tail folds into "Other".
import { useLayoutEffect, useRef, useState, type MouseEvent as RMouseEvent, type ReactNode } from "react";

/** Categorical palette, validated (lightness band, chroma floor, CVD and normal-vision separation, contrast). */
export const PALETTE = ["#008C99", "#DE6A2A", "#5B5FC7", "#6C9A1A", "#C8457F", "#2B88D8", "#B5871A", "#8A5CC0"];
export const OTHER = "#A19F9D";
export type ChartType = "column" | "stacked" | "bar" | "line" | "area" | "donut";
export type Series = { name: string; values: (number | null)[] };

export function niceTicks(lo: number, hi: number, n = 4) {
  if (lo === hi) { hi = lo === 0 ? 1 : lo * 1.1; if (lo > 0) lo = 0; }
  const span = hi - lo, raw = span / n, mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => span / s <= n) ?? 10 * mag;
  const out: number[] = [];
  const start = Math.floor(lo / step + 1e-9) * step, end = Math.ceil(hi / step - 1e-9) * step;
  for (let v = start; v <= end + step * 0.001; v += step) out.push(Math.round(v / step) * step);
  return out.length > 1 ? out : [start, start + step];
}
export const short = (n: number) => {
  const a = Math.abs(n);
  if (a >= 1e9) return `${(n / 1e9).toFixed(a >= 1e10 ? 0 : 1).replace(/\.0$/, "")}B`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(a >= 1e7 ? 0 : 1).replace(/\.0$/, "")}M`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(a >= 1e4 ? 0 : 1).replace(/\.0$/, "")}K`;
  return Number.isInteger(n) ? String(n) : n.toFixed(Math.abs(n) < 1 ? 2 : 1);
};

/** Keep the first eight series and sum the rest into "Other", so a hue is never reused. */
export function foldSeries(series: Series[], max = PALETTE.length): Series[] {
  if (series.length <= max) return series;
  const keep = series.slice(0, max - 1), rest = series.slice(max - 1);
  const len = Math.max(...series.map((s) => s.values.length));
  const other = Array.from({ length: len }, (_, i) => { const vs = rest.map((s) => s.values[i]).filter((v): v is number => v !== null); return vs.length ? vs.reduce((a, b) => a + b, 0) : null; });
  return [...keep, { name: `Other (${rest.length})`, values: other }];
}
export const colorOf = (s: Series, k: number) => (s.name.startsWith("Other (") ? OTHER : PALETTE[k % PALETTE.length]);

type Props = {
  type: ChartType; x: string[]; series: Series[]; label: string;
  format: (v: number | null, s: number) => string; pct?: boolean;
  height?: number; target?: number | null; dense?: boolean;
  /** A click on a mark: the category index and the series index (after folding into Other). */
  onPick?: (i: number, k: number) => void;
};

/** Top-rounded bar path: 2px data-end, square at the baseline (y0). */
function barPath(x: number, w: number, y0: number, y1: number, r = 2) {
  const up = y1 < y0, h = Math.abs(y1 - y0);
  const rr = Math.min(r, w / 2, h);
  if (h < 0.5) return "";
  if (up) return `M${x} ${y0}V${y1 + rr}Q${x} ${y1} ${x + rr} ${y1}H${x + w - rr}Q${x + w} ${y1} ${x + w} ${y1 + rr}V${y0}Z`;
  return `M${x} ${y0}V${y1 - rr}Q${x} ${y1} ${x + rr} ${y1}H${x + w - rr}Q${x + w} ${y1} ${x + w} ${y1 - rr}V${y0}Z`;
}
function hbarPath(y: number, h: number, x0: number, x1: number, r = 2) {
  const right = x1 > x0, w = Math.abs(x1 - x0);
  const rr = Math.min(r, h / 2, w);
  if (w < 0.5) return "";
  if (right) return `M${x0} ${y}H${x1 - rr}Q${x1} ${y} ${x1} ${y + rr}V${y + h - rr}Q${x1} ${y + h} ${x1 - rr} ${y + h}H${x0}Z`;
  return `M${x0} ${y}H${x1 + rr}Q${x1} ${y} ${x1} ${y + rr}V${y + h - rr}Q${x1} ${y + h} ${x1 + rr} ${y + h}H${x0}Z`;
}

export function Chart(props: Props) {
  const { type, label } = props;
  const box = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(600);
  const [BH, setBH] = useState(320);
  const [hover, setHover] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = box.current; if (!el) return;
    const size = () => { const cs = getComputedStyle(el); const px = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight), py = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom); setW(Math.max(260, el.clientWidth - px)); setBH(el.clientHeight - py); };
    const ro = new ResizeObserver(size);
    ro.observe(el); size();
    return () => ro.disconnect();
  }, []);
  const series = foldSeries(props.series);
  const empty = !props.x.length || !series.length || series.every((s) => s.values.every((v) => v === null || v === 0));
  // The legend sits under the plot, inside the same height.
  const H = (props.height ?? Math.max(240, Math.min(560, BH || 360))) - (series.length > 1 && type !== "donut" ? (W < 520 && series.length > 2 ? 46 : 26) : 0);
  return (
    <div className={`chart ${props.onPick ? "pickable" : ""}`} ref={box} style={props.height ? { height: props.height } : undefined} onMouseLeave={() => setHover(null)}>
      {empty ? <div className="chart-empty">No data for this chart in the selected period.</div>
        : type === "donut" ? <Donut {...props} series={series} W={W} H={H} hover={hover} setHover={setHover} />
        : type === "bar" ? <Bars {...props} series={series} W={W} hover={hover} setHover={setHover} />
        : <XY {...props} series={series} W={W} H={H} hover={hover} setHover={setHover} />}
      <span className="sr-only">{label}</span>
    </div>
  );
}

type Inner = Props & { series: Series[]; W: number; hover: number | null; setHover: (i: number | null) => void };

function Legend({ series, values }: { series: Series[]; values?: ReactNode[] }) {
  if (series.length < 2) return null;
  return <div className="ch-legend">{series.map((s, k) => <span key={k} className="ch-key"><i style={{ background: colorOf(s, k) }} />{s.name}{values?.[k] !== undefined ? <b>{values[k]}</b> : null}</span>)}</div>;
}

function Tip({ x, y, W, title, rows }: { x: number; y: number; W: number; title: string; rows: { color: string; name: string; value: string }[] }) {
  const left = x > W * 0.6 ? undefined : x + 14, right = x > W * 0.6 ? W - x + 14 : undefined;
  return (
    <div className="ch-tip" style={{ top: y, left, right }} role="status">
      <div className="ch-tip-t">{title}</div>
      {rows.map((r, i) => <div key={i} className="ch-tip-r"><i style={{ background: r.color }} /><span>{r.name}</span><b>{r.value}</b></div>)}
    </div>
  );
}

function XY({ type, x, series, format, pct, W, H, hover, setHover, target, dense, onPick }: Inner & { H: number }) {
  const stacked = type === "stacked" && series.length > 1;
  const legendH = series.length > 1 ? 0 : 0;
  // Value range: stacked sums positives and negatives separately.
  let lo = 0, hi = 0;
  if (stacked) x.forEach((_, i) => { let p = 0, n = 0; series.forEach((s) => { const v = s.values[i] ?? 0; if (v > 0) p += v; else n += v; }); hi = Math.max(hi, p); lo = Math.min(lo, n); });
  else for (const s of series) for (const v of s.values) if (v !== null && Number.isFinite(v)) { hi = Math.max(hi, v); lo = Math.min(lo, v); }
  if (target !== null && target !== undefined) { hi = Math.max(hi, target * 1.08); lo = Math.min(lo, target); }
  const ticks = niceTicks(lo, hi, H < 260 ? 3 : 4);
  const vmin = ticks[0], vmax = ticks.at(-1)!;
  const tickText = (t: number) => (pct ? `${Math.round(t * 100)}%` : short(t));
  const L = Math.max(...ticks.map((t) => tickText(t).length)) * 7 + 14, R = W - 8, T = 10 + legendH, B = H - 26;
  const Y = (v: number) => B - ((v - vmin) / (vmax - vmin)) * (B - T);
  const step = (R - L) / x.length;
  const cx = (i: number) => L + step * i + step / 2;
  const every = Math.max(1, Math.ceil(x.length / Math.max(1, Math.floor((R - L) / (dense ? 64 : 76)))));
  const groupW = Math.min(step * 0.72, stacked ? 24 : 24 * series.length + 2 * (series.length - 1));
  const bw = stacked ? groupW : Math.max(2, (groupW - 2 * (series.length - 1)) / series.length);
  const line = type === "line" || type === "area";
  const showDots = line && x.length <= 24;
  const tipRows = (i: number) => series.map((s, k) => ({ color: colorOf(s, k), name: s.name, value: format(s.values[i], k) }));
  const total = (i: number) => series.reduce((t, s) => t + (s.values[i] ?? 0), 0);
  /** Which series a click at this point lands on: the bar under it, the stacked segment, or the nearest line. */
  const seriesAt = (e: RMouseEvent<SVGRectElement>, i: number) => {
    if (series.length < 2) return 0;
    const box = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
    const mx = e.clientX - box.left, my = e.clientY - box.top;
    if (type === "column") return Math.max(0, Math.min(series.length - 1, Math.floor((mx - (cx(i) - groupW / 2)) / (bw + 2))));
    if (type === "stacked") {
      let pos = 0, neg = 0;
      for (let k = 0; k < series.length; k++) { const v = series[k].values[i] ?? 0; const a = v > 0 ? pos : neg, b = a + v; if (v > 0) pos = b; else neg = b; if (v && my <= Math.max(Y(a), Y(b)) && my >= Math.min(Y(a), Y(b))) return k; }
      return 0;
    }
    let best = 0, d = Infinity;
    series.forEach((s, k) => { const v = s.values[i]; if (v !== null && Math.abs(Y(v) - my) < d) { d = Math.abs(Y(v) - my); best = k; } });
    return best;
  };
  return (
    <>
      <svg width={W} height={H} role="img" aria-hidden="true">
        {ticks.map((t) => <g key={t}><line x1={L} x2={R} y1={Y(t)} y2={Y(t)} className={t === 0 ? "ax0" : "grid"} /><text x={L - 8} y={Y(t) + 4} textAnchor="end" className="tick">{tickText(t)}</text></g>)}
        {x.map((name, i) => i % every === 0 && <text key={i} x={cx(i)} y={B + 18} textAnchor="middle" className="cat">{name.length > 12 ? name.slice(0, 11) + "…" : name}</text>)}
        {hover !== null && <rect x={L + step * hover} y={T} width={step} height={B - T} className="hov" />}
        {type === "column" && series.map((s, k) => s.values.map((v, i) => {
          if (v === null) return null;
          const x0 = cx(i) - groupW / 2 + k * (bw + 2);
          return <path key={`${k}-${i}`} d={barPath(x0, bw, Y(0), Y(v))} fill={colorOf(s, k)} opacity={hover === null || hover === i ? 1 : 0.55} />;
        }))}
        {type === "stacked" && x.map((_, i) => {
          let pos = 0, neg = 0;
          const segs = series.map((s, k) => {
            const v = s.values[i] ?? 0; if (!v) return null;
            const a = v > 0 ? pos : neg, b = a + v;
            if (v > 0) pos = b; else neg = b;
            return { k, a, b };
          }).filter((z): z is { k: number; a: number; b: number } => !!z);
          const topPos = [...segs].reverse().find((z) => z.b > 0)?.k, topNeg = [...segs].reverse().find((z) => z.b < 0)?.k;
          return segs.map(({ k, a, b }) => {
            const y0 = Y(a), y1 = Y(b), gap = Math.abs(y1 - y0) > 3 ? 1 : 0; // 2px surface gap between segments
            const ya = y0 + (b > 0 ? -gap : gap), yb = y1 + (b > 0 ? gap : -gap);
            const top = k === topPos || k === topNeg;
            return <path key={`${k}-${i}`} d={top ? barPath(cx(i) - bw / 2, bw, ya, k === topPos || k === topNeg ? y1 : yb) : `M${cx(i) - bw / 2} ${ya}H${cx(i) + bw / 2}V${yb}H${cx(i) - bw / 2}Z`} fill={colorOf(series[k], k)} opacity={hover === null || hover === i ? 1 : 0.55} />;
          });
        })}
        {line && series.map((s, k) => {
          const pts = s.values.map((v, i) => (v === null ? null : [cx(i), Y(v)] as const));
          const segs: [number, number][][] = []; let cur: [number, number][] = [];
          pts.forEach((p) => { if (!p) { if (cur.length) segs.push(cur); cur = []; } else cur.push([p[0], p[1]]); });
          if (cur.length) segs.push(cur);
          const color = colorOf(s, k);
          const d = (seg: [number, number][]) => seg.map((p, j) => `${j ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join("");
          return (
            <g key={k} pointerEvents="none">
              {type === "area" && segs.map((seg, j) => <path key={`a${j}`} d={`${d(seg)}L${seg.at(-1)![0]} ${Y(Math.max(vmin, 0))}L${seg[0][0]} ${Y(Math.max(vmin, 0))}Z`} fill={color} opacity={0.1} />)}
              {segs.map((seg, j) => <path key={j} d={d(seg)} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />)}
              {pts.map((p, i) => p && (showDots || hover === i || (series.length === 1 && i === pts.length - 1)) && <circle key={i} cx={p[0]} cy={p[1]} r={4} fill={color} stroke="var(--surface)" strokeWidth={2} />)}
            </g>
          );
        })}
        {line && series.length === 1 && (() => {
          const i = series[0].values.length - 1, v = series[0].values[i];
          if (v === null || v === undefined) return null;
          return <text x={Math.min(R - 2, cx(i))} y={Y(v) - 10} textAnchor={cx(i) > R - 40 ? "end" : "middle"} className="ch-end">{format(v, 0)}</text>;
        })()}
        {target !== null && target !== undefined && <g><line x1={L} x2={R} y1={Y(target)} y2={Y(target)} className="ch-target" /><text x={L + 6} y={Y(target) - 6} textAnchor="start" className="ch-target-t">Target {pct ? `${Math.round(target * 1000) / 10}%` : short(target)}</text></g>}
        {x.map((_, i) => <rect key={i} x={L + step * i} y={T} width={step} height={B - T} fill="transparent" onMouseEnter={() => setHover(i)}
          onClick={onPick ? (e) => onPick(i, seriesAt(e, i)) : undefined} />)}
      </svg>
      <Legend series={series} />
      {hover !== null && <Tip x={cx(hover)} y={T + 6} W={W} title={x[hover]} rows={[...tipRows(hover), ...(stacked ? [{ color: "transparent", name: "Total", value: format(total(hover), 0) }] : [])]} />}
    </>
  );
}

function Bars({ x, series, format, pct, W, hover, setHover, onPick }: Inner) {
  const all = series.flatMap((s) => s.values).filter((v): v is number => v !== null && Number.isFinite(v));
  const ticks = niceTicks(Math.min(0, ...all), Math.max(0, ...all), 4);
  const vmin = ticks[0], vmax = ticks.at(-1)!;
  const labelW = Math.min(200, Math.max(70, Math.max(...x.map((s) => s.length)) * 6.5 + 12));
  const band = Math.max(22, Math.min(36, 14 * series.length + 12));
  const H = 10 + x.length * band + 24;
  const L = labelW, R = W - 12, T = 6;
  const X = (v: number) => L + ((v - vmin) / (vmax - vmin)) * (R - L);
  const bh = Math.min(16, (band - 8 - 2 * (series.length - 1)) / series.length);
  return (
    <>
      <svg width={W} height={H} role="img" aria-hidden="true">
        {ticks.map((t) => <g key={t}><line x1={X(t)} x2={X(t)} y1={T} y2={H - 22} className={t === 0 ? "ax0" : "grid"} /><text x={X(t)} y={H - 6} textAnchor="middle" className="tick">{pct ? `${Math.round(t * 100)}%` : short(t)}</text></g>)}
        {x.map((name, i) => (
          <g key={i} onMouseEnter={() => setHover(i)} onClick={onPick ? (e) => { const box = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect(); const off = e.clientY - box.top - T - i * band - (band - (bh * series.length + 2 * (series.length - 1))) / 2; onPick(i, Math.max(0, Math.min(series.length - 1, Math.floor(off / (bh + 2))))); } : undefined}>
            <rect x={0} y={T + i * band} width={W} height={band} className={hover === i ? "hov" : "hit"} />
            <text x={L - 8} y={T + i * band + band / 2 + 4} textAnchor="end" className="cat">{name.length > 28 ? name.slice(0, 27) + "…" : name}</text>
            {series.map((s, k) => { const v = s.values[i]; if (v === null) return null; const y = T + i * band + (band - (bh * series.length + 2 * (series.length - 1))) / 2 + k * (bh + 2); return <path key={k} d={hbarPath(y, bh, X(0), X(v))} fill={colorOf(s, k)} />; })}
          </g>
        ))}
      </svg>
      <Legend series={series} />
      {hover !== null && <Tip x={L + 20} y={T + hover * band + band} W={W} title={x[hover]} rows={series.map((s, k) => ({ color: colorOf(s, k), name: s.name, value: format(s.values[hover], k) }))} />}
    </>
  );
}

/** Part of a whole, at a glance: at most six slices (the rest fold into Other), with a legend of values and shares. */
function Donut({ x, series, format, W, H, hover, setHover, onPick }: Inner & { H: number }) {
  const vals = x.map((name, i) => ({ name, i, v: Math.max(0, series[0]?.values[i] ?? 0) })).filter((d) => d.v > 0).sort((a, b) => b.v - a.v);
  const slices = vals.length > 6 ? [...vals.slice(0, 5), { name: `Other (${vals.length - 5})`, i: -1, v: vals.slice(5).reduce((t, d) => t + d.v, 0) }] : vals;
  const pick = (k: number) => { if (onPick && slices[k] && slices[k].i >= 0) onPick(slices[k].i, 0); };
  const total = slices.reduce((t, d) => t + d.v, 0);
  const side = W >= 420;
  const size = Math.max(120, Math.min(side ? W * 0.45 : W, H - (side ? 16 : 120), 260));
  const r = size / 2, ri = r * 0.62, c = r;
  let a0 = -Math.PI / 2;
  const gap = slices.length > 1 ? 0.012 : 0;
  const arcs = slices.map((d, k) => {
    const sweep = (d.v / total) * Math.PI * 2;
    const s = a0 + gap, e = a0 + sweep - gap; a0 += sweep;
    const p = (ang: number, rad: number) => `${(c + rad * Math.cos(ang)).toFixed(2)} ${(c + rad * Math.sin(ang)).toFixed(2)}`;
    const large = e - s > Math.PI ? 1 : 0;
    const path = sweep >= Math.PI * 2 - 0.001
      ? `M${p(0, r)}A${r} ${r} 0 1 1 ${p(Math.PI, r)}A${r} ${r} 0 1 1 ${p(0, r)}M${p(0, ri)}A${ri} ${ri} 0 1 0 ${p(Math.PI, ri)}A${ri} ${ri} 0 1 0 ${p(0, ri)}Z`
      : `M${p(s, r)}A${r} ${r} 0 ${large} 1 ${p(e, r)}L${p(e, ri)}A${ri} ${ri} 0 ${large} 0 ${p(s, ri)}Z`;
    return { ...d, k, path, color: d.name.startsWith("Other (") ? OTHER : PALETTE[k] };
  });
  const focus = hover !== null ? arcs[hover] : null;
  return (
    <div className={`donut ${side ? "side" : ""}`}>
      <svg width={size} height={size} role="img" aria-hidden="true" style={{ flex: "0 0 auto" }}>
        {arcs.map((a, i) => <path key={i} d={a.path} fill={a.color} fillRule="evenodd" opacity={hover === null || hover === i ? 1 : 0.45} onMouseEnter={() => setHover(i)} onClick={() => pick(i)} />)}
        <text x={c} y={c - 2} textAnchor="middle" className="donut-v">{focus ? `${Math.round((focus.v / total) * 100)}%` : short(total)}</text>
        <text x={c} y={c + 16} textAnchor="middle" className="donut-l">{focus ? (focus.name.length > 16 ? focus.name.slice(0, 15) + "…" : focus.name) : "Total"}</text>
      </svg>
      <div className="donut-legend">
        {arcs.map((a, i) => (
          <div key={i} className={`donut-row ${hover === i ? "on" : ""}`} onMouseEnter={() => setHover(i)} onClick={() => pick(i)}>
            <i style={{ background: a.color }} /><span className="donut-n" title={a.name}>{a.name}</span>
            <span className="donut-p">{Math.round((a.v / total) * 100)}%</span><b>{format(a.v, 0)}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

/** A small trend line for a KPI: the de-emphasized history, the latest point in the accent. */
export function Spark({ values, width = 120, height = 32, good }: { values: (number | null)[]; width?: number; height?: number; good?: boolean | null }) {
  const pts = values.map((v, i) => [i, v] as const).filter((p): p is readonly [number, number] => p[1] !== null && Number.isFinite(p[1]));
  if (pts.length < 2) return null;
  const lo = Math.min(...pts.map((p) => p[1])), hi = Math.max(...pts.map((p) => p[1]));
  const X = (i: number) => 2 + (i / (values.length - 1)) * (width - 6), Y = (v: number) => height - 4 - ((v - lo) / (hi - lo || 1)) * (height - 8);
  const d = pts.map((p, j) => `${j ? "L" : "M"}${X(p[0]).toFixed(1)} ${Y(p[1]).toFixed(1)}`).join("");
  const last = pts.at(-1)!;
  return (
    <svg className="spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
      <path d={`${d}L${X(last[0])} ${height}L${X(pts[0][0])} ${height}Z`} fill="var(--brand)" opacity={0.08} />
      <path d={d} fill="none" stroke="#8A8886" strokeWidth={1.5} strokeLinejoin="round" />
      <circle cx={X(last[0])} cy={Y(last[1])} r={3.5} fill={good === false ? "#C4314B" : "var(--brand)"} stroke="var(--surface)" strokeWidth={1.5} />
    </svg>
  );
}
